import { create } from 'zustand';

import { api } from '../lib/api.js';
import { WEBSITE } from '../lib/edition.js';

/**
 * Local generation (images, pixel art, speech, music, sound effects, video and
 * 3D models made by generators running on this PC, and local language models
 * through Ollama): off until the user enables it and tells Prism where each
 * tool lives, in Settings → Local generation. The website has none of it.
 */

export interface LocalGenConfig {
  enabled: boolean;
  /** Where downloaded generator models live (HF cache). */
  modelsDir: string;
  /** Folder of the audio/pixel Python environments (env-speech, env-music, env-sfx, env-pixel). */
  audioDir: string;
  /** ComfyUI portable install folder (contains python_embeded and ComfyUI). */
  comfyDir: string;
  /** ComfyUI address. */
  comfyUrl: string;
  /** Extra ComfyUI workflow folder. */
  comfyWorkflows: string;
  /** ollama.exe (local language models). */
  ollamaExe: string;
  /** Folder Ollama keeps its models in. */
  ollamaModels: string;
  /** Ollama's OpenAI-compatible address. */
  ollamaUrl: string;
  /** ffmpeg.exe for the Video Studio (empty: ffmpeg on PATH). */
  ffmpegExe: string;
}

export interface LocalGenStatus {
  config: LocalGenConfig;
  /** Per tool: why it cannot run yet (null when it can). */
  problems: Record<string, string | null>;
}

interface LocalGenState {
  status: LocalGenStatus | null;
  load: () => Promise<LocalGenStatus | null>;
  save: (patch: Partial<LocalGenConfig>) => Promise<void>;
}

export const useLocalGen = create<LocalGenState>((set) => ({
  status: null,
  load: async () => {
    if (WEBSITE) return null;
    try {
      const status = await api.get<LocalGenStatus>('/local-gen');
      set({ status });
      return status;
    } catch {
      return null;
    }
  },
  save: async (patch) => {
    const status = await api.put<LocalGenStatus>('/local-gen', patch);
    set({ status });
  },
}));

/** Whether local generation is on. Not loaded yet counts as off. */
export function localGenEnabled(): boolean {
  return !WEBSITE && useLocalGen.getState().status?.config.enabled === true;
}

/**
 * For every local-generation button: true when it may go ahead; otherwise the
 * Local generation settings open (so the user can enable and configure it) and
 * the caller does nothing.
 */
export function requireLocalGen(): boolean {
  // No local generation anywhere: every generator runs on Prism Engine Cloud's own AI.
  return false;
}
